// src/hooks/useContracts.js - FIXED ACHIEVEMENT PARSING
import { useState, useEffect } from 'react';
import { formatEther, parseEther } from 'viem';
import { CONFIG, ABIS } from '../config/constants';

export const useContracts = (publicClient, walletClient, account) => {
  const [stats, setStats] = useState({ 
    totalSupply: 0, 
    remaining: 10000, 
    price: '0.002' 
  });
  const [saleActive, setSaleActive] = useState(false);
  const [nftBalance, setNftBalance] = useState(0);
  const [pogeBalance, setPogeBalance] = useState('0');
  const [stakedTokens, setStakedTokens] = useState([]);
  const [pendingRewards, setPendingRewards] = useState('0');
  const [emissionRate, setEmissionRate] = useState('0');
  const [rewardPoolBalance, setRewardPoolBalance] = useState('0');
  const [isApproved, setIsApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pogeStats, setPogeStats] = useState({
    rewardPool: '0'
  });
  const [userAchievements, setUserAchievements] = useState({
    isEarlyAdopter: false,
    hasDiamondPaws: false,
    isCollector: false,
    isPogeWhale: false,
    activeStakesCount: 0,
    totalEarnedPoge: '0',
    firstStakeTimestamp: 0,
    peakConcurrentStakes: 0,
    userLongestStakeDuration: 0
  });

  useEffect(() => {
    if (!publicClient) return;
    
    const fetchData = async () => {
      try {
        // Fetch NFT contract stats
        const [supply, maxSupply, price, active] = await Promise.all([
          publicClient.readContract({ 
            address: CONFIG.ADDRESSES.NFT, 
            abi: ABIS.NFT, 
            functionName: 'totalSupply' 
          }),
          publicClient.readContract({ 
            address: CONFIG.ADDRESSES.NFT, 
            abi: ABIS.NFT, 
            functionName: 'MAX_SUPPLY' 
          }),
          publicClient.readContract({ 
            address: CONFIG.ADDRESSES.NFT, 
            abi: ABIS.NFT, 
            functionName: 'PRICE' 
          }),
          publicClient.readContract({ 
            address: CONFIG.ADDRESSES.NFT, 
            abi: ABIS.NFT, 
            functionName: 'saleActive' 
          })
        ]);
        
        setStats({ 
          totalSupply: Number(supply), 
          remaining: Number(maxSupply) - Number(supply), 
          price: formatEther(price) 
        });
        setSaleActive(active);

        if (account) {
          // Fetch all user data in parallel
          const [balance, poge, approved, stakingInfo, emission, poolBalance, achievementsData] = await Promise.all([
            // NFT balance
            publicClient.readContract({ 
              address: CONFIG.ADDRESSES.NFT, 
              abi: ABIS.NFT, 
              functionName: 'balanceOf', 
              args: [account] 
            }),
         
            
            // POGE balance
            publicClient.readContract({ 
              address: CONFIG.ADDRESSES.COIN, 
              abi: ABIS.COIN, 
              functionName: 'balanceOf', 
              args: [account] 
            }),
            // Approval status
            publicClient.readContract({ 
              address: CONFIG.ADDRESSES.NFT, 
              abi: ABIS.NFT, 
              functionName: 'isApprovedForAll', 
              args: [account, CONFIG.ADDRESSES.STAKING] 
            }),
            // Staking info
            publicClient.readContract({
              address: CONFIG.ADDRESSES.STAKING,
              abi: ABIS.STAKING,
              functionName: 'getUserStakeInfo',
              args: [account]
            }).catch(() => [[], 0n, 0n]),
            // Emission rate
            publicClient.readContract({
              address: CONFIG.ADDRESSES.STAKING,
              abi: ABIS.STAKING,
              functionName: 'getCurrentEmissionRate'
            }).catch(() => 0n),
            // Reward pool balance
            publicClient.readContract({
              address: CONFIG.ADDRESSES.STAKING,
              abi: ABIS.STAKING,
              functionName: 'getRewardPoolBalance'
            }).catch(() => 0n),
            // User achievements - CORRECT PARSING
            publicClient.readContract({
              address: CONFIG.ADDRESSES.STAKING,
              abi: ABIS.STAKING,
              functionName: 'getUserAchievements',
              args: [account]
            }).catch(() => [false, false, false, false, 0n, 0n, 0n, 0n, 0n])
          ]);
          
          // Set basic user data
          setNftBalance(Number(balance));
          setPogeBalance(formatEther(poge));
          setIsApproved(approved || false);
          
          // Set staking info
          if (Array.isArray(stakingInfo) && stakingInfo.length >= 2) {
            setStakedTokens(stakingInfo[0]?.map(n => Number(n)) || []);
            const pendingAmount = formatEther(stakingInfo[2] || 0n); // Index 2 is totalPendingRewards
            setPendingRewards(pendingAmount);
            
            console.log('📊 STAKING INFO DEBUG:');
            console.log('  Token IDs:', stakingInfo[0]?.length || 0);
            console.log('  Active Count:', Number(stakingInfo[1] || 0n));
            console.log('  Total Pending:', pendingAmount);
          }
          
          // Set emission and pool data
          setEmissionRate(formatEther(emission || 0n));
          const poolBalanceFormatted = formatEther(poolBalance || 0n);
          setRewardPoolBalance(poolBalanceFormatted);
          
          setPogeStats({
            rewardPool: poolBalanceFormatted
          });
          
          // Parse achievements data - CORRECT STRUCTURE
          if (achievementsData && Array.isArray(achievementsData) && achievementsData.length >= 9) {
            setUserAchievements({
              isEarlyAdopter: achievementsData[0] || false,
              hasDiamondPaws: achievementsData[1] || false,
              isCollector: achievementsData[2] || false,
              isPogeWhale: achievementsData[3] || false,
              activeStakesCount: Number(achievementsData[4] || 0n),
              totalEarnedPoge: formatEther(achievementsData[5] || 0n),
              firstStakeTimestamp: Number(achievementsData[6] || 0n),
              peakConcurrentStakes: Number(achievementsData[7] || 0n),
              userLongestStakeDuration: Number(achievementsData[8] || 0n)
            });
          }
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };
    
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [publicClient, account]);

  // ============ ACTION FUNCTIONS ============

  const mint = async (quantity) => {
    if (!walletClient || !publicClient) {
      alert('Please connect your wallet first!');
      return;
    }

    try {
      const currentSaleActive = await publicClient.readContract({
        address: CONFIG.ADDRESSES.NFT,
        abi: ABIS.NFT,
        functionName: 'saleActive'
      });
      
      if (!currentSaleActive) {
        alert('⚠️ Sale is not active! Cannot mint.');
        return;
      }
    } catch (error) {
      console.error('Error checking sale status:', error);
      alert('Error: Could not verify sale status. Please try again.');
      return;
    }

    if (quantity > stats.remaining) {
      alert(`⚠️ Only ${stats.remaining} NFTs remaining! Please reduce quantity.`);
      return;
    }

    setLoading(true);
    try {
      const pricePerNFT = parseEther(stats.price);
      const value = pricePerNFT * BigInt(quantity);
      
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.NFT,
        abi: ABIS.NFT,
        functionName: 'mint',
        args: [BigInt(quantity)],
        value
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        alert('✅ Mint successful! 🎉');
      } else {
        alert('❌ Transaction failed! Check the transaction on BaseScan for details.');
      }
    } catch (error) {
      console.error('Mint error:', error);
      
      if (error.message?.includes('User rejected') || error.message?.includes('rejected')) {
        alert('Transaction cancelled.');
      } else {
        alert('❌ Mint failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const approveStaking = async () => {
    if (!walletClient || !publicClient) return;
    
    setLoading(true);
    try {
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.NFT,
        abi: ABIS.NFT,
        functionName: 'setApprovalForAll',
        args: [CONFIG.ADDRESSES.STAKING, true]
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        setIsApproved(true);
        alert('✅ Approval successful! You can now stake.');
      }
    } catch (error) {
      console.error('Approval error:', error);
      if (!error.message?.includes('rejected')) {
        alert('❌ Approval failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const revokeApproval = async () => {
    if (!walletClient || !publicClient) return;
    
    setLoading(true);
    try {
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.NFT,
        abi: ABIS.NFT,
        functionName: 'setApprovalForAll',
        args: [CONFIG.ADDRESSES.STAKING, false]
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        setIsApproved(false);
        alert('✅ Approval revoked!');
      }
    } catch (error) {
      console.error('Revoke error:', error);
      if (!error.message?.includes('rejected')) {
        alert('❌ Revoke failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const stake = async (tokenIds) => {
    if (!walletClient || !publicClient) return;
    
    if (!isApproved) {
      alert('⚠️ Please approve staking contract first!');
      return;
    }
    
    setLoading(true);
    try {
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.STAKING,
        abi: ABIS.STAKING,
        functionName: 'stake',
        args: [tokenIds.map(id => BigInt(id))]
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        alert('✅ Staked successfully! 🎉');
      }
    } catch (error) {
      console.error('Stake error:', error);
      if (!error.message?.includes('rejected')) {
        alert('❌ Stake failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const unstake = async (tokenIds) => {
    if (!walletClient || !publicClient) return;
    
    setLoading(true);
    try {
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.STAKING,
        abi: ABIS.STAKING,
        functionName: 'unstake',
        args: [tokenIds.map(id => BigInt(id))]
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        alert('✅ Unstaked successfully! 🎉');
      }
    } catch (error) {
      console.error('Unstake error:', error);
      if (!error.message?.includes('rejected')) {
        alert('❌ Unstake failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const claim = async () => {
    if (!walletClient || !publicClient) return;
    
    setLoading(true);
    try {
      const hash = await walletClient.writeContract({
        address: CONFIG.ADDRESSES.STAKING,
        abi: ABIS.STAKING,
        functionName: 'claimRewards'
      });
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        alert('✅ Rewards claimed! 🎉');
      }
    } catch (error) {
      console.error('Claim error:', error);
      if (!error.message?.includes('rejected')) {
        alert('❌ Claim failed: ' + (error.shortMessage || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  return { 
    stats, 
    saleActive, 
    nftBalance, 
    pogeBalance, 
    stakedTokens, 
    pendingRewards, 
    emissionRate,
    rewardPoolBalance,
    pogeStats,
    userAchievements,
    isApproved, 
    loading, 
    mint, 
    approveStaking,
    revokeApproval,
    stake, 
    unstake, 
    claim
  };
};